/**
 * Batch Processes Constants
 *
 * Status visual configs and operation type configs used across
 * the shared, list, and detail subdomains.
 */

import {
  IconLoader2,
  IconCheck,
  IconX,
  IconCopy,
  IconToggleLeft,
  IconDownload,
  IconCloudDownload,
  IconPackageExport,
  IconCurrencyDollar,
  IconPlus,
  IconQuestionMark,
  IconDatabaseImport,
  IconBuildingWarehouse,
  IconShoppingCartDown,
  IconPencil,
  IconWand
} from '@tabler/icons-react';
import type { IconProps } from '@tabler/icons-react';
import type { FC } from 'react';
import type { TFunction } from 'i18next';

// =============================================================================
// STATUS CONFIGURATION
// =============================================================================

export type BatchProcessStatus = 'running' | 'completed' | 'errored' | 'canceled';

export type StatusConfig = {
  color: string;
  icon: FC<IconProps>;
  labelKey: string;
};

export const statusConfig: Record<BatchProcessStatus, StatusConfig> = {
  running: {
    color: 'blue',
    icon: IconLoader2,
    labelKey: 'batchProcesses_statuses_running'
  },
  completed: {
    color: 'primary',
    icon: IconCheck,
    labelKey: 'batchProcesses_statuses_completed'
  },
  errored: {
    color: 'red',
    icon: IconX,
    labelKey: 'batchProcesses_statuses_errored'
  },
  canceled: {
    color: 'gray',
    icon: IconX,
    labelKey: 'batchProcesses_statuses_canceled'
  }
};

// =============================================================================
// OPERATION TYPE CONFIGURATION
// =============================================================================

export type OperationName =
  | 'PublicationsDuplication'
  | 'PublicationStatusChanged'
  | 'PublicationBulkEdit'
  | 'ChannelPublicationImport'
  | 'ChannelOrderImport'
  | 'PublicationItemExtraction'
  | 'PublicationPriceChanged'
  | 'PublicationCreation'
  | 'PublicationSyncFromChannel'
  | 'ErpItemImport'
  | 'ErpStockImport'
  | 'ErpPriceImport';

export type OperationConfig = {
  icon: FC<IconProps>;
  color: string;
  labelKey: string;
  /**
   * Interpolation values for `labelKey`, for the operation kinds whose display name
   * is not a fixed string. Values here are THEMSELVES translation keys — resolve the
   * whole thing through `resolveOperationLabel` below rather than a bare
   * `t( config.labelKey )`, so every part reaches the locale files.
   */
  labelParams?: Record<string, string>;
};

export const operationConfig: Record<string, OperationConfig> = {
  PublicationsDuplication: {
    icon: IconCopy,
    color: 'violet',
    labelKey: 'batchProcesses_operations_PublicationsDuplication'
  },
  PublicationStatusChanged: {
    icon: IconToggleLeft,
    color: 'teal',
    labelKey: 'batchProcesses_operations_PublicationStatusChanged'
  },
  // The bulk-edit run has been emitted by the server since it shipped
  // (`PublicationBulkEditBatchProcessStarted` -> `PublicationBulkEdit`, registered in
  // the server's `eventNameToOperationNameMap` AND in the readmodel's projector), but
  // was never registered on THIS side. It therefore fell through to
  // `defaultOperationConfig` and every bulk edit rendered as a grey question mark with
  // the label "Operación desconocida" — the exact "batch operations with no title"
  // symptom, and precisely the "graceful degradation that hides breakage" the
  // neighbouring comments warn about.
  PublicationBulkEdit: {
    icon: IconPencil,
    color: 'teal',
    labelKey: 'batchProcesses_operations_PublicationBulkEdit'
  },
  ChannelPublicationImport: {
    icon: IconDownload,
    color: 'indigo',
    labelKey: 'batchProcesses_operations_ChannelPublicationImport'
  },
  // TV1-2379 — the channel ORDER seeding run (the seller's full past order
  // history), sibling of the publication import above. Registered here for the
  // same reason as every other kind: this map is a `Record<string, …>`, so a
  // missing entry does not break the build — it falls through to
  // `defaultOperationConfig` and renders a real, running order import as a grey
  // question-mark row. Graceful degradation that hides breakage.
  ChannelOrderImport: {
    icon: IconShoppingCartDown,
    color: 'indigo',
    labelKey: 'batchProcesses_operations_ChannelOrderImport'
  },
  PublicationItemExtraction: {
    icon: IconPackageExport,
    color: 'orange',
    labelKey: 'batchProcesses_operations_PublicationItemExtraction'
  },
  PublicationPriceChanged: {
    icon: IconCurrencyDollar,
    color: 'yellow',
    labelKey: 'batchProcesses_operations_PublicationPriceChanged'
  },
  PublicationCreation: {
    icon: IconPlus,
    color: 'cyan',
    labelKey: 'batchProcesses_operations_PublicationCreation'
  },
  PublicationSyncFromChannel: {
    icon: IconCloudDownload,
    color: 'blue',
    labelKey: 'batchProcesses_operations_PublicationSyncFromChannel'
  },
  // ERP-ONBOARDING slice 5 — the first non-publication kind. Registering it here is
  // what keeps it out of the `defaultOperationConfig` fallback, which would render a
  // real, running ERP import as a grey question-mark row.
  ErpItemImport: {
    icon: IconDatabaseImport,
    color: 'grape',
    labelKey: 'batchProcesses_operations_ErpItemImport'
  },
  // ERP-ONBOARDING slice 9 — the per-warehouse bulk stock sweep. Registered for the
  // same reason: an unregistered kind falls through to `defaultOperationConfig` and a
  // real, running stock import renders as a grey question-mark row — graceful
  // degradation that hides breakage rather than reporting it.
  ErpStockImport: {
    icon: IconBuildingWarehouse,
    color: 'grape',
    labelKey: 'batchProcesses_operations_ErpStockImport'
  },
  // ERP-ONBOARDING slice 10 — the per-price-list bulk price sweep. Registered for the
  // same reason: an unregistered kind falls through to `defaultOperationConfig` and a
  // real, running price import renders as a grey question-mark row — graceful degradation
  // that hides breakage rather than reporting it.
  ErpPriceImport: {
    icon: IconCurrencyDollar,
    color: 'grape',
    labelKey: 'batchProcesses_operations_ErpPriceImport'
  }
};

/**
 * Default operation config for unknown operation types.
 * Ensures the UI never crashes when a new type is added server-side.
 */
export const defaultOperationConfig: OperationConfig = {
  icon: IconQuestionMark,
  color: 'gray',
  labelKey: 'batchProcesses_operations_unknown'
};

/**
 * The operation-RUN batch kinds (TV1-2113 D12).
 *
 * Unlike every other kind, these names are MINTED AT PROJECTION TIME rather than
 * drawn from a fixed list: `batchProcesses.readmodel.ts` writes
 * `Operation:${operationType}:${phase}` — e.g. `Operation:bulk-publish:apply`. A
 * registry keyed by literal string therefore could never match one, so every
 * operation run (bulk publish, reprice, kit inference, …) rendered as an untitled
 * grey question-mark row from the day the projector shipped.
 *
 * Parsed rather than enumerated: `operationType` is the server's `OperationTypeSchema`
 * enum, which grows. An unrecognised type still produces a correctly-shaped label —
 * it falls back to the raw type name inside a translated frame — instead of
 * collapsing to "unknown".
 */
const OPERATION_RUN_PREFIX = 'Operation:';

export type ParsedOperationRun = { operationType: string; phase: string };

export const parseOperationRunName = ( operationName: string ): ParsedOperationRun | null => {
  if ( !operationName.startsWith( OPERATION_RUN_PREFIX )) return null;
  const [ operationType, phase ] = operationName.slice( OPERATION_RUN_PREFIX.length ).split( ':' );
  if ( !operationType || !phase ) return null;
  return { operationType, phase };
};

/**
 * Per-phase icon/colour. `compute` proposes (nothing has changed yet), `apply`
 * commits — worth telling apart at a glance in a list of runs.
 */
const operationRunPhaseVisuals: Record<string, Pick<OperationConfig, 'icon' | 'color'>> = {
  compute: { icon: IconWand, color: 'violet' },
  apply: { icon: IconWand, color: 'grape' }
};

/**
 * Get operation config by name, with fallback to default.
 */
export const getOperationConfig = ( operationName: string ): OperationConfig => {
  const registered = operationConfig[operationName];
  if ( registered ) return registered;

  const run = parseOperationRunName( operationName );
  if ( run ) {
    return {
      ...( operationRunPhaseVisuals[run.phase] ?? { icon: IconWand, color: 'violet' }),
      labelKey: 'batchProcesses_operations_operationRun',
      labelParams: {
        operation: `batchProcesses_operations_operationTypes_${run.operationType}`,
        phase: `batchProcesses_operations_phases_${run.phase}`
      }
    };
  }

  return defaultOperationConfig;
};

/**
 * Resolve an operation's display label to translated text.
 *
 * The single place that knows `labelParams` values are keys, not literals: each is
 * run through `t()` with the raw token as its own default, so an operation type the
 * front end has never heard of degrades to `bulk-publish · Aplicar` rather than to
 * "Operación desconocida" — and the FRAME around it is still localised.
 *
 * `t` is passed in rather than imported so this stays a pure function usable from
 * the card, the mobile row and the detail header alike.
 */
export const resolveOperationLabel = (
  operationName: string,
  t: TFunction
): string => {
  const config = getOperationConfig( operationName );
  if ( !config.labelParams ) return t( config.labelKey );

  const resolved = Object.fromEntries(
    Object.entries( config.labelParams ).map(([ name, key ]) => [
      name,
      // The last path segment is the raw server token — the honest fallback when a
      // locale file has no entry for a newly-added operation type or phase.
      t( key, { defaultValue: key.slice( key.lastIndexOf( '_' ) + 1 ) })
    ])
  );
  return t( config.labelKey, resolved );
};
